
import { Component, Watch, Mixins, Prop } from 'vue-property-decorator'
import PaymentMixin from '~/components/common/modals/payment/PaymentMixin'
import Icon from '~/components/common/icon/Icon.vue'
import { notifyUnloadFunc } from '~/lib/utils/utils'

const style = {
  base: {
    fontSize: '18px',
    fontWeight: '400'
  }
}

@Component({
  components: {
    Icon
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', notifyUnloadFunc)
  }
})
export default class PaymentFormStripe extends Mixins(PaymentMixin) {
  @Prop({ type: String, default: '' }) redirectToUrl: string

  stripe = null
  elements = null
  fields = {
    cardNumber: {
      selector: '#card-number',
      opts: { placeholder: 'Card Number', style }
    },
    cardExpiry: {
      selector: '#card-expiration-date',
      opts: { style }
    },
    cardCvc: {
      selector: '#card-cvc',
      opts: { placeholder: 'CVV', style }
    }
  }

  cardElementErrors = {
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  }

  isSending = false

  intentType = ''
  intentClientSecret = ''
  paymentMethod = null

  @Watch('isSending', { immediate: true })
  notifyUnload(isSending:boolean) {
    if (isSending) {
      window.addEventListener('beforeunload', notifyUnloadFunc)
    } else {
      window.removeEventListener('beforeunload', notifyUnloadFunc)
    }
  }

  created() {
    if (!this.isStripeDefined()) { return }

    const stripePublishableKey = this.$accessor.account.paymentDetails.stripePublishableKey
    this.stripe = window.Stripe(stripePublishableKey)
    this.elements = this.stripe.elements({ loader: 'always' })

    this.initPayPal({
      success: this.paymentSuccessHandler,
      fail: this.errorHandler
    })
  }

  mounted() {
    for (const key of Object.keys(this.fields)) {
      const element = this.elements.create(key, this.fields[key].opts)
      element.mount(this.fields[key].selector)
      this.fields[key] = element

      element.on('change', (event) => {
        this.setErrorMessage(event)
      })
    }
  }

  beforeUnmount() {
    this.elements.destroy()
    this.teardownPayPal()
  }

  isStripeDefined() {
    if (!window.Stripe) {
      this.$sentry.captureException(new Error('Sentry instance is not defined'))
    }
    return !!window.Stripe
  }

  setErrorMessage({ elementType, error }) {
    if (!error && !this.cardElementErrors[elementType]) { return }
    this.cardElementErrors[elementType] = error ? error.message : ''
  }

  handleConformationResult(result) {
    if (result.error) { throw result.error }

    const handlers = {
      succeeded: (result) => {
        const payload = {
          packageId: this.packageId,
          intentId: result[this.intentType].id,
          paymentMethodId: result[this.intentType].payment_method
        }

        return this.$api.payment.stripeSubscribe(payload, {})
          .then((res) => {
            return this.paymentSuccessHandler(res.data.data)
          })
          .catch(async(res) => {
            const handleError = (res) => {
              const message = res?.body?.message ? res.body.message : 'Unknown error happened'
              this.errorHandler({ message })
            }

            if (res.status === 419) { // SRF token mismatch
              await this.$api.payment.refreshCSRF()
              return this.$api.payment.stripeSubscribe(payload, {})
                .then(res => this.paymentSuccessHandler(res.data.data))
                .catch((res) => {
                  handleError(res)
                })
            } else {
              handleError(res)
            }
          })
      },
      requires_action: (result) => {
        this.$emit('redirect-url', result[this.intentType].next_action.redirect_to_url.url)
        return Promise.resolve(this.redirectToUrl)
      },
      requires_payment_method: (result) => {
        this.$emit('redirect-url', '')
        const errorProp = this.intentType === 'setupIntent' ? 'last_setup_error' : 'last_payment_error'
        const message = result[this.intentType][errorProp].message
        this.errorHandler({ message })
      }
    }

    const status = result[this.intentType].status
    return handlers[status](result)
  }

  getMessageListener(ev) {
    if (ev.data !== '3DS-authentication-complete') { return }

    const retrieveIntent = this.isRecurrent ? 'retrieveSetupIntent' : 'retrievePaymentIntent'
    this.stripe[retrieveIntent](this.intentClientSecret)
      .then(async(result) => {
        await this.handleConformationResult(result)
      })
  }

  @Watch('redirectToUrl')
  onRedirectToUrlChange(val) {
    const func = val
      ? window.addEventListener
      : window.removeEventListener

    func('message', this.getMessageListener, false)
  }

  submit(ev) {
    ev.preventDefault()

    if (this.serverError) {
      this.serverError = ''
    }

    if (this.isSending) {
      return
    }

    this.isSending = true

    this.$api.payment.stripeIntent({ packageId: this.packageId })
      .then((res) => {
        return res.data.data
      })
      .catch((err) => {
        if (err?.body?.error) {
          this.$sentry.captureException(Error(err.body.error))
          throw new Error(err.body.error)
        }

        err?.body?.message && this.$sentry.captureException(Error(err.body.message))
        throw new Error('Unexpected error happened. Please try again.')
      })
      .then((result) => {
        this.intentType = result.intentType
        this.intentClientSecret = result.clientSecret

        const options = {
          payment_method: {
            card: this.fields.cardNumber
          },
          return_url: window.location.origin + '/payment-success'
        }

        if (this.intentType === 'setupIntent') {
          return this.stripe.confirmCardSetup(result.clientSecret, options, { handleActions: false })
        } else {
          return this.stripe.confirmCardPayment(result.clientSecret, options, { handleActions: false })
        }
      })
      .then(async(result) => {
        await this.handleConformationResult(result)
      })
      .catch((error) => {
        this.isSending = false
        this.errorHandler(error)
      })
      .finally(() => {
        this.isSending = false
        this.paymentMethod = null
      })
  }
}
